import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Container,
  Typography,
  Paper,
  Button,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Divider,
  Stack,
  IconButton,
  Chip,
  alpha,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  uploadSoftwareUpdateByChannel,
  deleteSoftwareUpdateByChannel,
  SoftwareUpdateResponse,
  getSoftwareUpdateInformation,
  createSoftwareUpdateInformation,
  updateSoftwareUpdateInformation,
  SoftwareChannel,
  getSoftwareUpdateAdminByChannel,
} from '../../services/api';
import { rewriteToCdn } from '../../utils/cdn';
import { useI18n } from '../../i18n/useI18n';

const channelTone = (channel: SoftwareChannel) => {
  if (channel === 'lite') return '#0284c7';
  if (channel === 'mono') return '#4b5563';
  return '#c2410c';
};

const SoftwareUpdate: React.FC = () => {
  const { admin: a, pages: t, formatMessage } = useI18n();
  const [updateInfo, setUpdateInfo] = useState<SoftwareUpdateResponse | null>(null);
  const [activeChannel, setActiveChannel] = useState<SoftwareChannel>('pro');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Update Information states
  const [updateInformation, setUpdateInformation] = useState<string>('');
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [savingInfo, setSavingInfo] = useState(false);
  const [hasInformation, setHasInformation] = useState(false);

  const fetchUpdateInfo = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSoftwareUpdateAdminByChannel(activeChannel);
      console.log('Software update data:', data);
      setUpdateInfo(data);
    } catch (error: any) {
      console.error('Error fetching software update:', error);
      console.error('Error response:', error?.response?.data);
      const errorMessage = error?.response?.data?.error || error?.message || 'Không thể tải thông tin cập nhật';
      toast.error(errorMessage);
      // Set updateInfo to null if there's an error
      setUpdateInfo(null);
    } finally {
      setLoading(false);
    }
  }, [activeChannel]);

  useEffect(() => {
    fetchUpdateInfo();
  }, [fetchUpdateInfo]);

  useEffect(() => {
    fetchUpdateInformation();
  }, []);

  const isApkChannel = activeChannel === 'lite' || activeChannel === 'mono';

  const handleFileUpload = async (file: File) => {
    const lower = file.name.toLowerCase();
    if (isApkChannel) {
      if (!lower.endsWith('.apk')) {
        toast.error('M-Photo Lite / Mono chỉ cho phép file .apk');
        return;
      }
    } else if (!lower.endsWith('.exe')) {
      toast.error('M-Photo Pro chỉ cho phép file .exe');
      return;
    }

    // Validate file size (1GB)
    if (file.size > 1024 * 1024 * 1024) {
      toast.error('File size không được vượt quá 1GB');
      return;
    }

    try {
      setUploading(true);
      await uploadSoftwareUpdateByChannel(activeChannel, file);
      toast.success('Upload thành công!');
      await fetchUpdateInfo();
    } catch (error: any) {
      console.error('Error uploading software update:', error);
      toast.error(error?.response?.data?.error || 'Không thể upload file');
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: isApkChannel
      ? {
          'application/vnd.android.package-archive': ['.apk'],
          'application/octet-stream': ['.apk'],
        }
      : {
          'application/x-msdownload': ['.exe'],
          'application/octet-stream': ['.exe'],
        },
    maxFiles: 1,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        await handleFileUpload(acceptedFiles[0]);
      }
    },
    onDropRejected: (fileRejections) => {
      fileRejections.forEach((rejection) => {
        if (rejection.errors.some((e) => e.code === 'file-invalid-type')) {
          toast.error(isApkChannel ? 'Chỉ cho phép file .apk' : 'Chỉ cho phép file .exe');
        } else if (rejection.errors.some((e) => e.code === 'too-many-files')) {
          toast.error('Chỉ có thể upload 1 file tại một thời điểm');
        } else {
          toast.error('File không hợp lệ');
        }
      });
    },
  });

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deleteSoftwareUpdateByChannel(activeChannel);
      toast.success('Đã xóa file update');
      setUpdateInfo(null);
      setDeleteDialogOpen(false);
    } catch (error: any) {
      console.error('Error deleting software update:', error);
      toast.error(error?.response?.data?.error || 'Không thể xóa file');
    } finally {
      setDeleting(false);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('vi-VN');
  };

  async function fetchUpdateInformation() {
    try {
      setLoadingInfo(true);
      const data = await getSoftwareUpdateInformation();
      setUpdateInformation(data.updateInformation || '');
      setHasInformation(data.hasInformation);
    } catch (error: any) {
      console.error('Error fetching update information:', error);
      // Không hiển thị lỗi nếu chưa có thông tin
      setUpdateInformation('');
      setHasInformation(false);
    } finally {
      setLoadingInfo(false);
    }
  }

  const handleSaveUpdateInformation = async () => {
    try {
      setSavingInfo(true);
      if (hasInformation) {
        await updateSoftwareUpdateInformation(updateInformation);
        toast.success('Cập nhật thông tin thành công!');
      } else {
        // Nếu chưa có, tạo mới
        try {
          await createSoftwareUpdateInformation(updateInformation);
          setHasInformation(true);
          toast.success('Tạo thông tin update thành công!');
        } catch (createError: any) {
          // Nếu lỗi vì đã tồn tại, thử update
          if (createError?.response?.status === 400) {
            await updateSoftwareUpdateInformation(updateInformation);
            setHasInformation(true);
            toast.success('Cập nhật thông tin thành công!');
          } else {
            throw createError;
          }
        }
      }
    } catch (error: any) {
      console.error('Error saving update information:', error);
      toast.error(error?.response?.data?.error || 'Không thể lưu thông tin update');
    } finally {
      setSavingInfo(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        mt: { xs: 1.5, sm: 3 },
        mb: { xs: 3, sm: 4 },
        px: { xs: 1.5, sm: 3 },
        '@keyframes swSoftIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
      }}
    >
      <Box
        sx={{
          mb: { xs: 1.5, sm: 2.5 },
          p: { xs: 1.75, sm: 2.5 },
          borderRadius: { xs: 3, sm: 4 },
          border: '1px solid',
          borderColor: alpha('#f48fb1', 0.25),
          background: `linear-gradient(135deg, ${alpha('#fff', 0.95)} 0%, ${alpha('#ffc1e3', 0.35)} 100%)`,
          boxShadow: `0 10px 28px ${alpha('#bf5f82', 0.1)}`,
          animation: 'swSoftIn 0.45s ease both',
        }}
      >
        <Typography
          component="h1"
          sx={{
            fontWeight: 800,
            fontSize: { xs: '1.35rem', sm: '1.85rem' },
            letterSpacing: '-0.02em',
            color: '#2d2430',
          }}
        >
          {a.softwareUpdate.title}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          mb: { xs: 1.5, sm: 2.5 },
          animation: 'swSoftIn 0.45s ease both',
          animationDelay: '0.05s',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 0.75,
            p: 0.5,
            borderRadius: 999,
            bgcolor: alpha('#f48fb1', 0.08),
            border: '1px solid',
            borderColor: alpha('#f48fb1', 0.18),
          }}
        >
          {(['pro', 'lite', 'mono'] as SoftwareChannel[]).map((channel) => {
            const selected = activeChannel === channel;
            const tone = channelTone(channel);
            return (
              <Button
                key={channel}
                onClick={() => setActiveChannel(channel)}
                sx={{
                  minWidth: 0,
                  py: { xs: 0.85, sm: 1 },
                  px: 0.5,
                  borderRadius: 999,
                  textTransform: 'uppercase',
                  fontWeight: 800,
                  fontSize: { xs: '0.72rem', sm: '0.85rem' },
                  color: selected ? '#fff' : tone,
                  bgcolor: selected ? tone : 'transparent',
                  boxShadow: selected ? `0 6px 14px ${alpha(tone, 0.35)}` : 'none',
                  '&:hover': {
                    bgcolor: selected ? tone : alpha(tone, 0.1),
                  },
                }}
              >
                {channel}
              </Button>
            );
          })}
        </Box>
        <IconButton
          onClick={fetchUpdateInfo}
          disabled={loading}
          aria-label={a.softwareUpdate.refresh}
          sx={{
            flexShrink: 0,
            width: { xs: 42, sm: 44 },
            height: { xs: 42, sm: 44 },
            border: '1px solid',
            borderColor: alpha('#f48fb1', 0.35),
            bgcolor: '#fff',
            color: 'primary.dark',
          }}
        >
          <RefreshIcon />
        </IconButton>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper
            {...getRootProps()}
            elevation={0}
            sx={{
              p: { xs: 2.25, sm: 4 },
              mb: { xs: 1.75, sm: 3 },
              textAlign: 'center',
              borderRadius: { xs: 3, sm: 4 },
              cursor: uploading ? 'wait' : 'pointer',
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : alpha('#f48fb1', 0.45),
              background: isDragActive
                ? alpha('#f48fb1', 0.12)
                : `linear-gradient(165deg, #fff 0%, ${alpha('#ffc1e3', 0.18)} 100%)`,
              transition: 'all 0.25s ease',
              animation: 'swSoftIn 0.45s ease both',
              animationDelay: '0.1s',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: alpha('#f48fb1', 0.08),
              },
            }}
          >
            <input {...getInputProps()} disabled={uploading} />
            <Box
              sx={{
                width: { xs: 56, sm: 72 },
                height: { xs: 56, sm: 72 },
                mx: 'auto',
                mb: { xs: 1.25, sm: 2 },
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha('#f48fb1', 0.16),
                color: 'primary.dark',
              }}
            >
              <UploadIcon sx={{ fontSize: { xs: 30, sm: 40 } }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: '0.9rem', sm: '1.15rem' },
                lineHeight: 1.35,
                px: { xs: 0.5, sm: 0 },
                color: '#2d2430',
              }}
            >
              {isDragActive
                ? a.softwareUpdate.dropActive
                : isApkChannel
                  ? a.softwareUpdate.dropIdleApk
                  : a.softwareUpdate.dropIdleExe}
            </Typography>
            <Chip
              size="small"
              label={formatMessage(a.softwareUpdate.currentChannel, {
                channel: activeChannel.toUpperCase(),
              })}
              sx={{
                mt: 1.25,
                fontWeight: 800,
                bgcolor: alpha(channelTone(activeChannel), 0.12),
                color: channelTone(activeChannel),
              }}
            />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, fontSize: { xs: '0.72rem', sm: '0.875rem' } }}
            >
              {isApkChannel ? a.softwareUpdate.hintApk : a.softwareUpdate.hintExe}
            </Typography>
            {uploading && (
              <Box sx={{ mt: 2.5 }}>
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
                  {a.softwareUpdate.uploading}
                </Typography>
              </Box>
            )}
          </Paper>

          {updateInfo?.hasFile ? (
            <Card
              elevation={0}
              sx={{
                mb: { xs: 1.75, sm: 2.5 },
                borderRadius: { xs: 3, sm: 4 },
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: `0 8px 22px ${alpha('#bf5f82', 0.08)}`,
                overflow: 'hidden',
                animation: 'swSoftIn 0.45s ease both',
                animationDelay: '0.15s',
              }}
            >
              <CardContent sx={{ p: { xs: 1.75, sm: 2.5 }, '&:last-child': { pb: { xs: 1.75, sm: 2.5 } } }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.75 }}>
                  <Box
                    component="img"
                    src="/m-photo-logo.png"
                    alt="M-Photo Logo"
                    sx={{
                      width: { xs: 52, sm: 72 },
                      height: { xs: 52, sm: 72 },
                      borderRadius: '50%',
                      objectFit: 'cover',
                      flexShrink: 0,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1.15rem' } }}>
                      {a.softwareUpdate.currentFile}
                    </Typography>
                    <Chip
                      size="small"
                      label={activeChannel.toUpperCase()}
                      sx={{
                        mt: 0.5,
                        height: 22,
                        fontWeight: 800,
                        fontSize: '0.68rem',
                        bgcolor: alpha(channelTone(activeChannel), 0.12),
                        color: channelTone(activeChannel),
                      }}
                    />
                  </Box>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: { xs: 1, sm: 1.25 },
                  }}
                >
                  {[
                    { label: a.softwareUpdate.fileName, value: updateInfo.fileName },
                    { label: a.softwareUpdate.fileSize, value: formatFileSize(updateInfo.fileSize) },
                    { label: a.softwareUpdate.uploadDate, value: formatDate(updateInfo.dateUpload) },
                    updateInfo.uploadedBy
                      ? {
                          label: a.softwareUpdate.uploadedBy,
                          value: `${updateInfo.uploadedBy.username} (${updateInfo.uploadedBy.email})`,
                        }
                      : null,
                  ]
                    .filter(Boolean)
                    .map((row) => (
                      <Box
                        key={row!.label}
                        sx={{
                          p: 1.25,
                          borderRadius: 2.5,
                          bgcolor: alpha('#f48fb1', 0.06),
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: alpha('#2d2430', 0.5),
                            mb: 0.35,
                          }}
                        >
                          {row!.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: '0.82rem', sm: '0.9rem' },
                            fontWeight: 600,
                            color: '#2d2430',
                            wordBreak: 'break-word',
                          }}
                        >
                          {row!.value}
                        </Typography>
                      </Box>
                    ))}
                </Box>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  sx={{ mt: 1.75 }}
                >
                  {updateInfo.fileUrl && updateInfo.fileName && (
                    <Button
                      fullWidth
                      variant="contained"
                      component="a"
                      startIcon={<DownloadIcon />}
                      href={rewriteToCdn(updateInfo.fileUrl)}
                      download={updateInfo.fileName}
                      sx={{
                        borderRadius: 2.5,
                        textTransform: 'none',
                        fontWeight: 700,
                        py: 1,
                      }}
                    >
                      {a.softwareUpdate.download}
                    </Button>
                  )}
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteDialogOpen(true)}
                    disabled={deleting}
                    sx={{
                      borderRadius: 2.5,
                      textTransform: 'none',
                      fontWeight: 700,
                      py: 1,
                    }}
                  >
                    {t.common.delete}
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ) : (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 3, animation: 'swSoftIn 0.45s ease both', animationDelay: '0.15s' }}>
              {a.softwareUpdate.empty}
            </Alert>
          )}

          <Divider sx={{ my: { xs: 2.5, sm: 3.5 } }} />
          <Box sx={{ mb: 1.5, animation: 'swSoftIn 0.45s ease both', animationDelay: '0.2s' }}>
            <Typography
              component="h2"
              sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', sm: '1.35rem' } }}
            >
              {a.softwareUpdate.updateInfo}
            </Typography>
          </Box>

          {loadingInfo ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: { xs: 1.25, sm: 2.5 },
                mb: 2,
                borderRadius: { xs: 3, sm: 4 },
                border: '1px solid',
                borderColor: 'divider',
                animation: 'swSoftIn 0.45s ease both',
                animationDelay: '0.24s',
                '& .ql-toolbar': {
                  borderRadius: '12px 12px 0 0',
                  flexWrap: 'wrap',
                },
                '& .ql-container': {
                  borderRadius: '0 0 12px 12px',
                  minHeight: { xs: 180, sm: 260 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                },
              }}
            >
              <ReactQuill
                theme="snow"
                value={updateInformation}
                onChange={setUpdateInformation}
                modules={quillModules}
                style={{ marginBottom: 12 }}
              />
              <Box sx={{ display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' }, mt: 1.5 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveUpdateInformation}
                  disabled={savingInfo}
                  sx={{
                    maxWidth: { sm: 220 },
                    borderRadius: 2.5,
                    textTransform: 'none',
                    fontWeight: 700,
                    py: 1.1,
                  }}
                >
                  {savingInfo ? a.softwareUpdate.saving : a.softwareUpdate.saveInfo}
                </Button>
              </Box>
            </Paper>
          )}
        </>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{a.softwareUpdate.deleteTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {a.softwareUpdate.deleteConfirm}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            {t.common.cancel}
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={deleting}>
            {deleting ? a.softwareUpdate.deleting : t.common.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SoftwareUpdate;

